import { User, Pencil } from "lucide-react";
import { Badge } from "../ui/Badge";
import { getOverallConsentStatus } from "../../lib/consent-utils";
import type { UserListItem } from "../../lib/types";
import { UserEditForm } from "./UserEditForm";
import type { ConsentFormValues } from "../../lib/consent-schema";

interface UserListProps {
  groupedUsers: Record<
    string,
    { name: string; users: UserListItem[] }
  >;
  primaryGroupId: string | undefined;
  editingUserId: string | null;
  onEditUser: (id: string) => void;
  onCancelEdit: () => void;
  onSaveUser: (id: string, data: ConsentFormValues) => void;
}

export function UserList({
  groupedUsers,
  primaryGroupId,
  editingUserId,
  onEditUser,
  onCancelEdit,
  onSaveUser,
}: UserListProps) {
  return (
    <>
      {Object.entries(groupedUsers).map(([id, group]) => (
        <div key={id} className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
            {group.name}
          </h4>
          <div className="grid grid-cols-1 gap-4">
            {group.users.map((u) => {
              const isEditable = u.groups?.some(
                (g) => g.group.id === primaryGroupId,
              );
              const isEditing = editingUserId === u.id;

              return (
                <div
                  key={u.id}
                  className="bg-white shadow sm:rounded-lg p-4 flex items-center justify-between"
                >
                  {isEditing ? (
                    <UserEditForm
                      user={u}
                      onSave={(data) => onSaveUser(u.id, data)}
                      onCancel={onCancelEdit}
                    />
                  ) : (
                    <>
                      <div className="flex items-center space-x-4">
                        <div className="bg-gray-100 p-2 rounded-full">
                          <User className="w-5 h-5 text-gray-500" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {u.firstName} {u.lastName}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="text-sm text-gray-500">
                              {u.email}
                            </div>
                            {getOverallConsentStatus(u) === "active" ? (
                              <Badge variant="success" size="sm">
                                Consent Active
                              </Badge>
                            ) : (
                              <Badge variant="warning" size="sm">
                                Consent Review Required
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      {isEditable && (
                        <button
                          onClick={() => onEditUser(u.id)}
                          className="p-2 text-gray-400 hover:text-primary-600 rounded-full hover:bg-gray-100"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
}

